<template>
  <div class="language-dropdown">
    <VaDropdown placement="bottom-end" :offset="[0, 10]">
      <template #anchor>
        <button class="glass-toggle" type="button" aria-label="Change language">
          <VaIcon name="language" size="small" />
          <span class="code">{{ locale.toUpperCase() }}</span>
          <VaIcon class="chevron" name="expand_more" size="small" />
        </button>
      </template>

      <VaDropdownContent class="dropdown-content">
        <button
          v-for="(name, code) in languages"
          :key="code"
          :class="['dropdown-item', { active: locale === code }]"
          @click="locale = code"
        >
          <VaIcon name="language" size="small" />
          <span class="name">{{ name }} ({{ getFlag(code) }})</span>
          <VaIcon v-if="locale === code" name="check" size="small" />
        </button>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { onMounted, watch } from 'vue'
import { loadLocaleMessages } from '../../../i18n'

const getStorage = (): Storage => {
  if (sessionStorage.getItem('impersonation_session')) {
    return sessionStorage
  }

  if (sessionStorage.getItem('admin_token') && sessionStorage.getItem('role') === 'agent') {
    return sessionStorage
  }

  return localStorage
}

const { locale, availableLocales, setLocaleMessage } = useI18n()

onMounted(() => {
  const storage = getStorage()
  const storedLocale = storage.getItem('app_locale')
  if (storedLocale && storedLocale in languages) {
    locale.value = storedLocale
  }
})

watch(locale, async (newLocale) => {
  if (!availableLocales.includes(newLocale)) {
    const messages = await loadLocaleMessages(newLocale)
    setLocaleMessage(newLocale, messages)
  }

  const storage = getStorage()
  storage.setItem('app_locale', newLocale)

  window.dispatchEvent(new CustomEvent('languageChanged', { detail: { locale: newLocale } }))
})

const languages = {
  gb: 'English',
  cn: '中文',
}

const getFlag = (code: string): string => {
  const codes: Record<string, string> = { gb: 'GB', cn: 'CN' }
  return codes[code] || 'EN'
}
</script>

<style scoped>
.language-dropdown {
  min-width: 0;
}

.glass-toggle {
  height: 38px;
  padding: 0 0.8rem 0 0.7rem;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--va-background-border) 65%, transparent);
  background: color-mix(in srgb, var(--va-background-element) 58%, transparent);
  color: var(--va-on-background-primary);
  box-shadow: inset 0 1px 0 color-mix(in srgb, #fff 7%, transparent);
  cursor: pointer;
  font-size: 0.8125rem;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.15s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  white-space: nowrap;
}

.glass-toggle:hover {
  background: color-mix(in srgb, var(--va-primary) 12%, var(--va-background-element));
  border-color: color-mix(in srgb, var(--va-primary) 35%, var(--va-background-border));
  box-shadow:
    inset 0 1px 0 color-mix(in srgb, #fff 8%, transparent),
    0 6px 16px color-mix(in srgb, var(--va-primary) 12%, transparent);
}

.glass-toggle:active {
  transform: translateY(1px) scale(0.98);
}

.code {
  font-size: 0.72rem;
  font-weight: 750;
  letter-spacing: 0.08em;
  line-height: 1;
}

.chevron {
  opacity: 0.65;
  transition: transform 0.2s ease;
}

.dropdown-content {
  min-width: 196px;
  padding: 0.45rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  border-radius: 14px;
  border: 1px solid color-mix(in srgb, var(--va-background-border) 70%, transparent);
  background: color-mix(in srgb, var(--va-background-primary) 94%, transparent);
  backdrop-filter: blur(16px);
  box-shadow:
    0 18px 40px rgba(15, 23, 42, 0.16),
    0 2px 8px rgba(15, 23, 42, 0.06);
}

.dropdown-item {
  width: 100%;
  padding: 0.7rem 0.8rem;
  border: none;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  font-size: 0.875rem;
  color: var(--va-on-background-primary);
  transition: background-color 0.15s ease, color 0.15s ease;
  text-align: left;
}

.dropdown-item:hover {
  background: color-mix(in srgb, var(--va-primary) 10%, transparent);
}

.dropdown-item.active {
  background: color-mix(in srgb, var(--va-primary) 14%, transparent);
  color: var(--va-primary);
  font-weight: 650;
}

.name {
  flex: 1;
}

@media (max-width: 640px) {
  .glass-toggle {
    height: 36px;
    padding: 0 0.65rem;
  }
}

@media (max-width: 480px) {
  .glass-toggle {
    width: 34px;
    height: 34px;
    padding: 0;
    border-radius: 11px;
  }

  .code,
  .chevron {
    display: none;
  }
}
</style>
